import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { ArrowLeft, Printer, CheckSquare, ShieldAlert } from "lucide-react";

// Phase 18-B1 item 8: printable A5 how-to guide for a gathering template.
// Reuses the 17-C poster print pattern (.certificate-print / .no-print).
export default function ProgrammeGuide() {
  const { slug } = useParams();
  const [tpl, setTpl] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.gatheringTemplates().then((all) => {
      const t = all.find((x) => x.slug === slug);
      if (t) setTpl(t); else setMissing(true);
    }).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">This guide doesn't exist.</div>;
  if (!tpl) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="programme-guide-page">
      <Seo title={`${tpl.title} — how-to guide`} />
      <div className="no-print">
        <Link to="/collectives" className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back to collectives
        </Link>
      </div>
      <div className="flex items-center justify-between mt-4 no-print">
        <h1 className="font-display text-3xl text-forest tracking-tight">{tpl.title} — how-to</h1>
        <Button data-testid="guide-print-btn" onClick={() => window.print()} className="rounded-full bg-forest hover:bg-forest-hover">
          <Printer className="w-4 h-4 mr-2" /> Print (A5)
        </Button>
      </div>
      <p className="text-sm text-neutral-500 mt-2 no-print">
        Print it, pin it by the kettle, and hand a copy to your co-leader.
      </p>

      {/* The guide itself — A5 portrait proportions, print-visible only */}
      <div className="certificate-print mt-8 bg-white border-[6px] border-double border-forest rounded-2xl p-9 relative overflow-hidden" style={{ aspectRatio: "148/210" }} data-testid="guide-sheet">
        <div className="flex flex-col h-full">
          <div className="text-[10px] uppercase tracking-[0.3em] text-terracotta font-bold">CivicHearth programme kit</div>
          <h2 className="font-display text-3xl text-forest mt-2 tracking-tight" data-testid="guide-title">{tpl.title}</h2>
          <p className="text-sm text-neutral-700 mt-3 leading-relaxed" data-testid="guide-what-why">{tpl.what_why}</p>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
              <CheckSquare className="w-3.5 h-3.5" /> Checklist
            </div>
            <ul className="mt-2 space-y-1.5" data-testid="guide-checklist">
              {tpl.checklist.map((item, i) => (
                <li key={i} className="text-sm text-neutral-700 flex items-start gap-2">
                  <span className="inline-block w-3.5 h-3.5 border-2 border-forest/40 rounded-sm mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="guide-safety">
            <div className="text-xs uppercase tracking-[0.18em] text-amber-800 font-bold inline-flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" /> Safety
            </div>
            <p className="text-sm text-amber-900 mt-1.5 leading-relaxed">{tpl.safety}</p>
          </div>

          <div className="mt-auto pt-5 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">
              Civic<span className="text-terracotta">.</span>Hearth — volunteer locally, ripple globally
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
